import React from 'react';
import { usePermissions } from '../../hooks/usePermissions';

/**
 * PermissionWrapper Component
 * Conditionally renders children based on user permissions
 */
const PermissionWrapper = ({ 
  children, 
  section, 
  permission, 
  action = 'view', 
  requireSensitive = false,
  fallback = null,
  className = ''
}) => {
  const permissions = usePermissions();

  /**
   * Check if user has the required permission
   */
  const hasAccess = () => {
    // If requireSensitive is true, check for sensitive access
    if (requireSensitive) {
      return permissions.canAccessSensitive();
    }

    // If specific permission is provided, check that
    if (permission) {
      return permissions.hasPermission(permission);
    }

    // If section and action are provided, use appropriate method
    if (section) {
      switch (action) {
        case 'view':
          return permissions.canView(section);
        case 'manage':
        case 'edit':
          return permissions.canManage(section);
        default:
          return permissions.canView(section);
      }
    }

    // Default to true if no specific requirements
    return true;
  };

  /**
   * Special case handlers
   */
  const specialCases = {
    createProjects: () => permissions.canCreateProjects(),
    generateReports: () => permissions.canGenerateReports(),
    syncToProduction: () => permissions.canSyncToProduction(),
    testChatbot: () => permissions.canTestChatbot(),
    viewBilling: () => permissions.canViewBilling(),
  };

  // Check for special cases
  if (section && specialCases[section]) {
    if (!specialCases[section]()) {
      return fallback;
    }
  } else if (!hasAccess()) {
    return fallback;
  }

  // Wrap children in a div with className if provided
  if (className) {
    return <div className={className}>{children}</div>;
  }

  return children;
};

/**
 * Specialized wrapper for main users only
 */
export const MainUserOnly = ({ children, fallback = null }) => {
  const { isMainUser } = usePermissions();
  return isMainUser ? children : fallback;
};

/**
 * Specialized wrapper for sub users only
 */
export const SubUserOnly = ({ children, fallback = null }) => {
  const { isSubUser } = usePermissions();
  return isSubUser ? children : fallback;
};

/**
 * Specialized wrapper for sensitive content (API keys, billing, etc.)
 */
export const SensitiveContentWrapper = ({ children, fallback = null }) => {
  return (
    <PermissionWrapper requireSensitive={true} fallback={fallback}>
      {children}
    </PermissionWrapper>
  );
};

/**
 * Read-only indicator component
 */
export const ReadOnlyIndicator = ({ show = true, className = '' }) => {
  if (!show) return null;
  
  return (
    <div className={`bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 ${className}`}>
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm text-blue-800">
            You have read-only access to this section. Contact your administrator to request editing permissions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PermissionWrapper; 